import React, { useEffect, useMemo } from "react";
import * as Yup from "yup";
import { Formik, Form, Field, FormikErrors, FormikTouched } from "formik";
import {
  REQUEST_CREATE_MASTER_COMMUNITY,
  REQUEST_MASTER_COMMUNITY,
  REQUEST_UPDATE_MASTER_COMMUNITY,
} from "../../store/community/communityActionTypes";
import { useDispatch } from "react-redux";
import {
  useToast,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from "@chakra-ui/react";
import { useCommunityMaster } from "../../store/community/reducer";

interface CommunityEditDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  commData?: any;
}

interface FormValues {
  name: string;
  description: string;
  email: string;
  phone: string;
  reason: string;
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters"),
  description: Yup.string()
    .required("Description is required")
    .min(2, "Description must be at least 2 characters"),
  email: Yup.string()
    .required("Email is required")
    .email("Invalid email format"),
  phone: Yup.string()
    .required("Phone number is required")
    .matches(/^\d{10}$/, "Phone number must be 10 digits"),
});

const CommunityEditDrawer: React.FC<CommunityEditDrawerProps> = ({
  isOpen,
  onClose,
  commData,
}) => {
  console.log("commData", commData);
  const { masterCommunity, busy, message } = useCommunityMaster();
  const dispatch = useDispatch();
  const toast = useToast();

  const initialValues: FormValues = useMemo(() => {
    if (commData) {
      return {
        name: commData?.name || "",
        description: commData?.description || "",
        email: commData?.email || "",
        phone: commData?.phone || "",
        reason: commData?.reason || "",
      };
    }
    return {
      name: "",
      description: "",
      email: "",
      phone: "",
      reason: "",
    };
  }, [commData]);

  const handleSubmit = (values: FormValues, { setSubmitting }: any) => {
    try {
      const payload = {
        name: values.name,
        description: values.description,
        email: values.email,
        phone: values.phone,
        reason: values.reason,
      };

      if (commData?._id) {
        dispatch({
          type: REQUEST_UPDATE_MASTER_COMMUNITY,
          data: {
            payload,
            _id: commData._id,
          },
          onSuccess: () => {
            dispatch({ type: REQUEST_MASTER_COMMUNITY });
            toast({
              title: "Success",
              description: "Community updated successfully",
              status: "success",
              duration: 3000,
              isClosable: true,
              position: "top-right",
            });
            onClose();
          },
        });
      } else {
        dispatch({
          type: REQUEST_CREATE_MASTER_COMMUNITY,
          payload,
          onSuccess: () => {
            dispatch({ type: REQUEST_MASTER_COMMUNITY });
            toast({
              title: "Success",
              description: "Community created successfully",
              status: "success",
              duration: 3000,
              isClosable: true,
              position: "top-right",
            });
            onClose();
          },
        });
      }

      setSubmitting(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while saving community data",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      setSubmitting(false);
    }
  };

  const prevCommunity = useMemo(() => {
    return masterCommunity;
  }, []);

  useEffect(() => {
    if (commData) {
      const communityFromStore = masterCommunity.find(
        (r: any) => r._id === commData._id
      );

      if (communityFromStore !== commData) {
        toast({
          title: "Success",
          description: "Community has been updated",
          status: "success",
          isClosable: true,
          position: "top-right",
        });
        onClose();
      }
    } else {
      if (masterCommunity.length !== prevCommunity.length) {
        toast({
          title: "Success",
          description: "New Community has been created",
          status: "success",
          isClosable: true,
          position: "top-right",
        });
        onClose();
      }
    }
  }, [masterCommunity, prevCommunity, commData, onClose, toast]);

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader borderBottomWidth="1px" bg="#e244411a">
          {commData ? "Edit" : "Add"} Community
        </DrawerHeader>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <DrawerBody>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "15px",
                  }}
                >
                  <FormControl isInvalid={!!(errors.name && touched.name)}>
                    <FormLabel htmlFor="name">Name</FormLabel>
                    <Field
                      name="name"
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                      }}
                    />
                    <FormErrorMessage>{errors.name}</FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isInvalid={!!(errors.description && touched.description)}
                  >
                    <FormLabel htmlFor="description">Description</FormLabel>
                    <Field
                      name="description"
                      as="textarea"
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        minHeight: "100px",
                      }}
                    />
                    <FormErrorMessage>{errors.description}</FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!(errors.reason && touched.reason)}>
                    <FormLabel htmlFor="reason">Reason</FormLabel>
                    <Field
                      name="reason"
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                      }}
                    />
                    <FormErrorMessage>{errors.reason}</FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!(errors.email && touched.email)}>
                    <FormLabel htmlFor="email">Email</FormLabel>
                    <Field
                      name="email"
                      type="email"
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                      }}
                    />
                    <FormErrorMessage>{errors.email}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!(errors.phone && touched.phone)}>
                    <FormLabel htmlFor="phone">Phone</FormLabel>
                    <Field
                      name="phone"
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                      }}
                    />
                    <FormErrorMessage>{errors.phone}</FormErrorMessage>
                  </FormControl>
                </div>
              </DrawerBody>

              <DrawerFooter borderTopWidth="1px">
                <Button
                  variant="outline"
                  mr={3}
                  onClick={onClose}
                  border="1px solid #EEE"
                  borderRadius="6px"
                  color="#424242"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  backgroundColor="#E24441"
                  color="white"
                  _hover={{ backgroundColor: "#E24441" }}
                >
                  {commData ? "Update" : "Add"} Community
                </Button>
              </DrawerFooter>
            </Form>
          )}
        </Formik>
      </DrawerContent>
    </Drawer>
  );
};

export default CommunityEditDrawer;
